/**
 * OAuth Login settings management page component for admins.
 */
import React, { useState, FormEvent, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { useSettings } from '../contexts/SettingsContext';
import { updateSettings, SettingsUpdate } from '../api/settings';
import { getApiUrl } from '../config/runtime';
import { HelpIcon } from '../components/Icons';
import './OAuthLoginPage.css';

export const OAuthLoginPage: React.FC = () => {
  const { settings, refreshSettings } = useSettings();
  // OAuth2 state
  const [oauth2Provider, setOAuth2Provider] = useState<string>('');
  const [oauth2ClientId, setOAuth2ClientId] = useState('');
  const [oauth2ClientSecret, setOAuth2ClientSecret] = useState('');
  const [oauth2AuthorizationUrl, setOAuth2AuthorizationUrl] = useState('');
  const [oauth2TokenUrl, setOAuth2TokenUrl] = useState('');
  const [oauth2UserinfoUrl, setOAuth2UserinfoUrl] = useState('');
  const [oauth2Scope, setOAuth2Scope] = useState('');
  const [oauth2CilogonDefaultIdp, setOAuth2CilogonDefaultIdp] = useState('');
  const [oauth2CilogonIdpList, setOAuth2CilogonIdpList] = useState('');
  const [oauth2SignupRequiresApproval, setOAuth2SignupRequiresApproval] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openHelpId, setOpenHelpId] = useState<string | null>(null);
  
  // OAuth2 is enabled if a provider is selected
  const oauth2Enabled = oauth2Provider !== '' && oauth2Provider !== 'disabled';

  useEffect(() => {
    if (settings) {
      // OAuth2 fields - if provider is null or 'disabled', set to 'disabled'
      if (settings.oauth2_provider && settings.oauth2_provider !== 'disabled') {
        setOAuth2Provider(settings.oauth2_provider);
      } else {
        setOAuth2Provider('disabled');
      }
      setOAuth2ClientId(settings.oauth2_client_id || '');
      setOAuth2ClientSecret(settings.oauth2_client_secret || '');
      setOAuth2AuthorizationUrl(settings.oauth2_authorization_url || '');
      setOAuth2TokenUrl(settings.oauth2_token_url || '');
      setOAuth2UserinfoUrl(settings.oauth2_userinfo_url || '');
      setOAuth2Scope(settings.oauth2_scope || '');
      setOAuth2CilogonDefaultIdp(settings.oauth2_cilogon_default_idp || '');
      setOAuth2CilogonIdpList(settings.oauth2_cilogon_idp_list || '');
      setOAuth2SignupRequiresApproval(settings.oauth2_signup_requires_approval ?? true);
    }
  }, [settings]);

  // Provider-specific defaults
  useEffect(() => {
    if (oauth2Provider && oauth2Provider !== 'disabled') {
      switch (oauth2Provider.toLowerCase()) {
        case 'google':
          if (!oauth2AuthorizationUrl) setOAuth2AuthorizationUrl('https://accounts.google.com/o/oauth2/v2/auth');
          if (!oauth2TokenUrl) setOAuth2TokenUrl('https://oauth2.googleapis.com/token');
          if (!oauth2UserinfoUrl) setOAuth2UserinfoUrl('https://www.googleapis.com/oauth2/v2/userinfo');
          if (!oauth2Scope) setOAuth2Scope('openid email profile');
          break;
        case 'github':
          if (!oauth2AuthorizationUrl) setOAuth2AuthorizationUrl('https://github.com/login/oauth/authorize');
          if (!oauth2TokenUrl) setOAuth2TokenUrl('https://github.com/login/oauth/access_token');
          if (!oauth2UserinfoUrl) setOAuth2UserinfoUrl('https://api.github.com/user');
          if (!oauth2Scope) setOAuth2Scope('user:email');
          break;
        case 'microsoft':
          if (!oauth2AuthorizationUrl) setOAuth2AuthorizationUrl('https://login.microsoftonline.com/common/oauth2/v2.0/authorize');
          if (!oauth2TokenUrl) setOAuth2TokenUrl('https://login.microsoftonline.com/common/oauth2/v2.0/token');
          if (!oauth2UserinfoUrl) setOAuth2UserinfoUrl('https://graph.microsoft.com/v1.0/me');
          if (!oauth2Scope) setOAuth2Scope('openid email profile');
          break;
        case 'cilogon':
          if (!oauth2AuthorizationUrl) setOAuth2AuthorizationUrl('https://cilogon.org/authorize');
          if (!oauth2TokenUrl) setOAuth2TokenUrl('https://cilogon.org/oauth2/token');
          if (!oauth2UserinfoUrl) setOAuth2UserinfoUrl('https://cilogon.org/oauth2/userinfo');
          if (!oauth2Scope) setOAuth2Scope('openid email profile');
          break;
        default:
          // Custom provider - no defaults
          break;
      }
    }
  }, [oauth2Provider, oauth2AuthorizationUrl, oauth2TokenUrl, oauth2UserinfoUrl, oauth2Scope]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      // Trim OAuth2 client ID and secret
      const trimmedClientId = oauth2ClientId?.trim() || null;
      const trimmedClientSecret = oauth2ClientSecret?.trim() || null;
      
      const updates: SettingsUpdate = {
        oauth2_provider: oauth2Enabled ? oauth2Provider : 'disabled',
        oauth2_client_id: oauth2Enabled ? trimmedClientId : null,
        oauth2_client_secret: oauth2Enabled ? trimmedClientSecret : null,
        oauth2_authorization_url: oauth2Enabled ? (oauth2AuthorizationUrl || null) : null,
        oauth2_token_url: oauth2Enabled ? (oauth2TokenUrl || null) : null,
        oauth2_userinfo_url: oauth2Enabled ? (oauth2UserinfoUrl || null) : null,
        oauth2_scope: oauth2Enabled ? (oauth2Scope || null) : null,
        oauth2_redirect_uri: (() => {
          try {
            const apiUrl = getApiUrl();
            const baseApiUrl = apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
            return `${baseApiUrl}/auth/oauth2/callback`;
          } catch {
            return 'http://localhost:8000/api/auth/oauth2/callback'; // Fallback
          }
        })(),
        oauth2_cilogon_default_idp: oauth2Enabled && oauth2Provider === 'cilogon' ? (oauth2CilogonDefaultIdp.trim() || null) : null,
        oauth2_cilogon_idp_list: oauth2Enabled && oauth2Provider === 'cilogon' ? (oauth2CilogonIdpList.trim() || null) : null,
        oauth2_signup_requires_approval: oauth2SignupRequiresApproval,
      };

      await updateSettings(updates);
      setSuccess('OAuth login settings updated successfully!');
      await refreshSettings();
    } catch (err: any) {
      let errorMessage = 'Failed to update OAuth login settings. Please try again.';
      
      if (err?.response?.data) {
        const errorData = err.response.data;
        
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      // Reset OAuth2 fields
      if (settings.oauth2_provider && settings.oauth2_provider !== 'disabled') {
        setOAuth2Provider(settings.oauth2_provider);
      } else {
        setOAuth2Provider('disabled');
      }
      setOAuth2ClientId(settings.oauth2_client_id || '');
      setOAuth2ClientSecret(settings.oauth2_client_secret || '');
      setOAuth2AuthorizationUrl(settings.oauth2_authorization_url || '');
      setOAuth2TokenUrl(settings.oauth2_token_url || '');
      setOAuth2UserinfoUrl(settings.oauth2_userinfo_url || '');
      setOAuth2Scope(settings.oauth2_scope || '');
      setOAuth2CilogonDefaultIdp(settings.oauth2_cilogon_default_idp || '');
      setOAuth2CilogonIdpList(settings.oauth2_cilogon_idp_list || '');
      setOAuth2SignupRequiresApproval(settings.oauth2_signup_requires_approval ?? true);
      setError(null);
      setSuccess(null);
    }
  };

  const handleResetToDefaults = () => {
    if (window.confirm('Are you sure you want to reset all OAuth login settings to defaults? This cannot be undone.')) {
      // Reset OAuth2 to defaults
      setOAuth2Provider('disabled');
      setOAuth2ClientId('');
      setOAuth2ClientSecret('');
      setOAuth2AuthorizationUrl('');
      setOAuth2TokenUrl('');
      setOAuth2UserinfoUrl('');
      setOAuth2Scope('');
      setOAuth2CilogonDefaultIdp('');
      setOAuth2CilogonIdpList('');
      setOAuth2SignupRequiresApproval(true);
      setError(null);
      setSuccess(null);
    }
  };

  // Close help popup when clicking outside or on the popup itself
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't close if clicking the help button (let the button handler toggle it)
      const helpButton = target.closest('.help-icon-button');
      if (helpButton) {
        return;
      }
      
      // Close if clicking on the popup content or outside
      const helpPopup = document.querySelector('.help-popup');
      if (helpPopup) {
        setOpenHelpId(null);
      }
    };

    if (openHelpId) {
      document.addEventListener('mousedown', handleClick);
      return () => {
        document.removeEventListener('mousedown', handleClick);
      };
    }
  }, [openHelpId]);

  // Help text for each field
  const helpTexts: Record<string, string> = {
    'oauth2-provider': 'Select the OAuth2 provider you want to use for authentication. Choose "Disabled" to turn off OAuth2 authentication. Pre-configured providers (Google, GitHub, Microsoft, CILogon) will automatically fill in their endpoint URLs.',
    'oauth2-redirect-uri': 'This is the callback URL that your OAuth2 provider will redirect to after authentication. You must register this exact URL in your OAuth2 provider\'s application settings. Copy this URL and paste it into your provider\'s redirect URI configuration.',
    'oauth2-client-id': 'The Client ID is a public identifier for your OAuth2 application. You obtain this from your OAuth2 provider when you register your application. This value is not secret and can be safely stored.',
    'oauth2-client-secret': 'The Client Secret is a confidential credential used to authenticate your application with the OAuth2 provider. Keep this secret secure. If you leave this field empty when updating settings, the existing secret will be preserved.',
    'oauth2-authorization-url': 'The authorization endpoint URL where users will be redirected to authenticate. This is provided by your OAuth2 provider. For custom providers, you must specify this URL manually.',
    'oauth2-token-url': 'The token endpoint URL where your application exchanges the authorization code for an access token. This is provided by your OAuth2 provider. For custom providers, you must specify this URL manually.',
    'oauth2-userinfo-url': 'The userinfo endpoint URL where your application retrieves user information using the access token. This is provided by your OAuth2 provider. For custom providers, you must specify this URL manually.',
    'oauth2-scope': 'OAuth2 scopes define what information your application can access. Common scopes include "openid" (required for OpenID Connect), "email" (access to email address), and "profile" (access to profile information). Separate multiple scopes with spaces.',
    'oauth2-cilogon-default-idp': 'The entityID of the default Identity Provider that will be pre-selected when users authenticate through CILogon. This makes it easier for users from your institution to log in. You can find available IDPs at https://cilogon.org/idplist/',
    'oauth2-cilogon-idp-list': 'A list of Identity Provider entityIDs that will be shown to users. Only the IDPs in this list will be displayed. If left empty, all available IDPs will be shown. You can enter one IDP per line or separate them with commas. Find available IDPs at https://cilogon.org/idplist/',
  };

  const toggleHelp = (helpId: string) => {
    setOpenHelpId(openHelpId === helpId ? null : helpId);
  };

  // Convert URLs in text to clickable links
  const renderHelpText = (text: string): React.ReactNode => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--primary-color)', textDecoration: 'underline' }}
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  if (!settings) {
    return (
      <AdminLayout>
        <div className="oauth-login-page">
          <div className="oauth-login-container">
            <div className="loading-message">Loading settings...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="oauth-login-page">
        <div className="oauth-login-container">
          <div className="admin-breadcrumb">
            <Link to="/admin">Admin</Link>
            <span className="admin-breadcrumb-separator">/</span>
            <span className="admin-breadcrumb-current">OAuth Login</span>
          </div>
          <h1>OAuth Login Settings</h1>

          <form onSubmit={handleSubmit} className="oauth-login-form" role="form">
            {error && (
              <div className="error-message" role="alert">
                <strong>Error:</strong> {error}
              </div>
            )}

            {success && (
              <div className="success-message" role="alert">
                {success}
              </div>
            )}

            <section className="settings-section">
              <div className="form-group">
                <label htmlFor="oauth2-provider">OAuth2 Provider</label>
                <select
                  id="oauth2-provider"
                  value={oauth2Provider}
                  onChange={(e) => {
                    setOAuth2Provider(e.target.value);
                    // Reset URLs, scope, and credentials when provider changes (provider-specific)
                    if (e.target.value === 'disabled') {
                      setOAuth2AuthorizationUrl('');
                      setOAuth2TokenUrl('');
                      setOAuth2UserinfoUrl('');
                      setOAuth2Scope('');
                      setOAuth2ClientId('');
                      setOAuth2ClientSecret('');
                    } else {
                      setOAuth2AuthorizationUrl('');
                      setOAuth2TokenUrl('');
                      setOAuth2UserinfoUrl('');
                      setOAuth2Scope('');
                      setOAuth2ClientId('');
                      setOAuth2ClientSecret('');
                    }
                    setError(null);
                  }}
                  disabled={isLoading}
                  className="select-input"
                >
                  <option value="disabled">Disabled</option>
                  <option value="google">Google</option>
                  <option value="github">GitHub</option>
                  <option value="microsoft">Microsoft</option>
                  <option value="cilogon">CILogon</option>
                  <option value="custom">Custom</option>
                </select>
                <small className="form-hint">
                  Select the OAuth2 provider or choose Disabled to turn off OAuth2 authentication
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="oauth2-redirect-uri" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  Redirect URI
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      toggleHelp('oauth2-redirect-uri');
                    }}
                    className="help-icon-button"
                    aria-label="Help for Redirect URI"
                  >
                    <HelpIcon width={16} height={16} />
                  </button>
                  {openHelpId === 'oauth2-redirect-uri' && (
                    <div className="help-popup">
                          {renderHelpText(helpTexts['oauth2-redirect-uri'])}
                    </div>
                  )}
                </label>
                <input
                  id="oauth2-redirect-uri"
                  type="url"
                  value={(() => {
                    try {
                      const apiUrl = getApiUrl();
                      const baseApiUrl = apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
                      return `${baseApiUrl}/auth/oauth2/callback`;
                    } catch {
                      return 'http://localhost:8000/api/auth/oauth2/callback'; // Fallback
                    }
                  })()}
                  readOnly
                  disabled={isLoading}
                  className="readonly-input"
                />
                <small className="form-hint">
                  This is the redirect URI you must configure in your OAuth2 provider.
                  Copy this URL to your OAuth2 application settings.
                </small>
              </div>

              {oauth2Enabled && (
                <div className="form-group">
                  <label className="checkbox-label" htmlFor="oauth2-signup-requires-approval">
                    <input
                      id="oauth2-signup-requires-approval"
                      type="checkbox"
                      checked={oauth2SignupRequiresApproval}
                      onChange={(e) => {
                        setOAuth2SignupRequiresApproval(e.target.checked);
                        setError(null);
                      }}
                      disabled={isLoading}
                    />
                    <span>Require Admin Approval for OAuth Sign-Ups</span>
                  </label>
                  <small className="form-hint">
                    When enabled, new users who sign up via OAuth2 will require admin approval before they can log in.
                    This setting is independent of the local login approval requirement.
                  </small>
                </div>
              )}

              {oauth2Enabled && (
                <>
                  <div className="form-group">
                    <label htmlFor="oauth2-client-id" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      Client ID
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          toggleHelp('oauth2-client-id');
                        }}
                        className="help-icon-button"
                        aria-label="Help for Client ID"
                      >
                        <HelpIcon width={16} height={16} />
                      </button>
                      {openHelpId === 'oauth2-client-id' && (
                        <div className="help-popup">
                          {renderHelpText(helpTexts['oauth2-client-id'])}
                        </div>
                      )}
                    </label>
                    <input
                      id="oauth2-client-id"
                      type="text"
                      value={oauth2ClientId}
                      onChange={(e) => {
                        setOAuth2ClientId(e.target.value);
                        setError(null);
                      }}
                      onBlur={(e) => {
                        setOAuth2ClientId(e.target.value.trim());
                      }}
                      disabled={isLoading}
                      placeholder="Enter OAuth2 client ID"
                      required={oauth2Enabled}
                    />
                    <small className="form-hint">
                      Your OAuth2 application client ID from the provider
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="oauth2-client-secret" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      Client Secret
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          toggleHelp('oauth2-client-secret');
                        }}
                        className="help-icon-button"
                        aria-label="Help for Client Secret"
                      >
                        <HelpIcon width={16} height={16} />
                      </button>
                      {openHelpId === 'oauth2-client-secret' && (
                        <div className="help-popup">
                          {renderHelpText(helpTexts['oauth2-client-secret'])}
                        </div>
                      )}
                    </label>
                    <input
                      id="oauth2-client-secret"
                      type="password"
                      value={oauth2ClientSecret}
                      onChange={(e) => {
                        setOAuth2ClientSecret(e.target.value);
                        setError(null);
                      }}
                      onBlur={(e) => {
                        setOAuth2ClientSecret(e.target.value.trim());
                      }}
                      disabled={isLoading}
                      placeholder="Enter OAuth2 client secret (leave empty to keep existing secret)"
                    />
                    <small className="form-hint">
                      Your OAuth2 application client secret from the provider (stored securely). 
                      Leave empty to keep the existing secret when updating other settings.
                    </small>
                  </div>

                  {oauth2Provider === 'custom' && (
                    <>
                      <div className="form-group">
                        <label htmlFor="oauth2-authorization-url" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          Authorization URL
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              toggleHelp('oauth2-authorization-url');
                            }}
                            className="help-icon-button"
                            aria-label="Help for Authorization URL"
                          >
                            <HelpIcon width={16} height={16} />
                          </button>
                          {openHelpId === 'oauth2-authorization-url' && (
                            <div className="help-popup">
                              {renderHelpText(helpTexts['oauth2-authorization-url'])}
                            </div>
                          )}
                        </label>
                        <input
                          id="oauth2-authorization-url"
                          type="url"
                          value={oauth2AuthorizationUrl}
                          onChange={(e) => {
                            setOAuth2AuthorizationUrl(e.target.value);
                            setError(null);
                          }}
                          disabled={isLoading}
                          placeholder="https://provider.com/oauth2/authorize"
                          required={oauth2Enabled && oauth2Provider === 'custom'}
                        />
                        <small className="form-hint">
                          OAuth2 authorization endpoint URL
                        </small>
                      </div>

                      <div className="form-group">
                        <label htmlFor="oauth2-token-url" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          Token URL
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              toggleHelp('oauth2-token-url');
                            }}
                            className="help-icon-button"
                            aria-label="Help for Token URL"
                          >
                            <HelpIcon width={16} height={16} />
                          </button>
                          {openHelpId === 'oauth2-token-url' && (
                            <div className="help-popup">
                              {renderHelpText(helpTexts['oauth2-token-url'])}
                            </div>
                          )}
                        </label>
                        <input
                          id="oauth2-token-url"
                          type="url"
                          value={oauth2TokenUrl}
                          onChange={(e) => {
                            setOAuth2TokenUrl(e.target.value);
                            setError(null);
                          }}
                          disabled={isLoading}
                          placeholder="https://provider.com/oauth2/token"
                          required={oauth2Enabled && oauth2Provider === 'custom'}
                        />
                        <small className="form-hint">
                          OAuth2 token exchange endpoint URL
                        </small>
                      </div>

                      <div className="form-group">
                        <label htmlFor="oauth2-userinfo-url" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          User Info URL
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              toggleHelp('oauth2-userinfo-url');
                            }}
                            className="help-icon-button"
                            aria-label="Help for User Info URL"
                          >
                            <HelpIcon width={16} height={16} />
                          </button>
                          {openHelpId === 'oauth2-userinfo-url' && (
                            <div className="help-popup">
                              {renderHelpText(helpTexts['oauth2-userinfo-url'])}
                            </div>
                          )}
                        </label>
                        <input
                          id="oauth2-userinfo-url"
                          type="url"
                          value={oauth2UserinfoUrl}
                          onChange={(e) => {
                            setOAuth2UserinfoUrl(e.target.value);
                            setError(null);
                          }}
                          disabled={isLoading}
                          placeholder="https://provider.com/oauth2/userinfo"
                          required={oauth2Enabled && oauth2Provider === 'custom'}
                        />
                        <small className="form-hint">
                          OAuth2 user info endpoint URL
                        </small>
                      </div>
                    </>
                  )}

                  <div className="form-group">
                    <label htmlFor="oauth2-scope" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      Scope
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          toggleHelp('oauth2-scope');
                        }}
                        className="help-icon-button"
                        aria-label="Help for Scope"
                      >
                        <HelpIcon width={16} height={16} />
                      </button>
                      {openHelpId === 'oauth2-scope' && (
                        <div className="help-popup">
                          {renderHelpText(helpTexts['oauth2-scope'])}
                        </div>
                      )}
                    </label>
                    <input
                      id="oauth2-scope"
                      type="text"
                      value={oauth2Scope}
                      onChange={(e) => {
                        setOAuth2Scope(e.target.value);
                        setError(null);
                      }}
                      disabled={isLoading}
                      placeholder="openid email profile"
                      required={oauth2Enabled}
                    />
                    <small className="form-hint">
                      OAuth2 scopes to request (e.g., "openid email profile")
                    </small>
                  </div>

                  {oauth2Provider === 'cilogon' && (
                    <>
                      <div className="form-group">
                        <label htmlFor="oauth2-cilogon-default-idp" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          Default IDP
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              toggleHelp('oauth2-cilogon-default-idp');
                            }}
                            className="help-icon-button"
                            aria-label="Help for Default IDP"
                          >
                            <HelpIcon width={16} height={16} />
                          </button>
                          {openHelpId === 'oauth2-cilogon-default-idp' && (
                            <div className="help-popup">
                              {renderHelpText(helpTexts['oauth2-cilogon-default-idp'])}
                            </div>
                          )}
                        </label>
                        <input
                          id="oauth2-cilogon-default-idp"
                          type="text"
                          value={oauth2CilogonDefaultIdp}
                          onChange={(e) => {
                            setOAuth2CilogonDefaultIdp(e.target.value);
                            setError(null);
                          }}
                          disabled={isLoading}
                          placeholder="https://idp.example.edu/idp/shibboleth"
                        />
                        <small className="form-hint">
                          Default Identity Provider entityID for CILogon (optional). 
                          This IDP will be pre-selected when users authenticate.
                          <br />
                          <a 
                            href="https://cilogon.org/idplist/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: 'var(--primary-color)' }}
                          >
                            View list of available IDPs →
                          </a>
                        </small>
                      </div>

                      <div className="form-group">
                        <label htmlFor="oauth2-cilogon-idp-list" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          List of IDPs
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              toggleHelp('oauth2-cilogon-idp-list');
                            }}
                            className="help-icon-button"
                            aria-label="Help for List of IDPs"
                          >
                            <HelpIcon width={16} height={16} />
                          </button>
                          {openHelpId === 'oauth2-cilogon-idp-list' && (
                            <div className="help-popup">
                              {renderHelpText(helpTexts['oauth2-cilogon-idp-list'])}
                            </div>
                          )}
                        </label>
                        <textarea
                          id="oauth2-cilogon-idp-list"
                          value={oauth2CilogonIdpList}
                          onChange={(e) => {
                            setOAuth2CilogonIdpList(e.target.value);
                            setError(null);
                          }}
                          disabled={isLoading}
                          placeholder="https://idp1.example.edu/idp/shibboleth&#10;https://idp2.example.edu/idp/shibboleth&#10;https://accounts.google.com"
                          rows={4}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid var(--border-color)',
                            borderRadius: '4px',
                            fontSize: '1rem',
                            backgroundColor: 'var(--background)',
                            color: 'var(--text-color)',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                          }}
                        />
                        <small className="form-hint">
                          List of allowed Identity Provider entityIDs for CILogon (one per line or comma-separated, optional).
                          Only these IDPs will be shown to users. If empty, all available IDPs will be shown.
                          <br />
                          <a 
                            href="https://cilogon.org/idplist/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: 'var(--primary-color)' }}
                          >
                            View list of available IDPs →
                          </a>
                        </small>
                      </div>
                    </>
                  )}

                </>
              )}
            </section>

            <div className="form-actions">
              <button
                type="button"
                className="btn-reset"
                onClick={handleReset}
                disabled={isLoading}
              >
                Reset Changes
              </button>
              <button
                type="button"
                className="btn-reset-defaults"
                onClick={handleResetToDefaults}
                disabled={isLoading}
              >
                Reset to Defaults
              </button>
              <button
                type="submit"
                className="btn-save"
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

